import { useCallback, useEffect, useState } from "react";
import { HiArrowLeft, HiEllipsisVertical, HiChevronDown, HiExclamationTriangle } from "react-icons/hi2";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import CircleInfo from "../design-system/icons/CircleInfo.jsx";
import CircleCheck from "../design-system/icons/CircleCheck.jsx";
import useAuthedApi from "../hooks/useAuthedApi";
import Badge from "../design-system/badge/Badge.jsx";
import BotActivityChart from "./BotActivityChart.jsx";

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtUsdt(val, dp = 4) {
  const n = parseFloat(val);
  if (isNaN(n)) return "—";
  return n.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

function fmtPct(val, dp = 2) {
  const n = parseFloat(val);
  if (isNaN(n)) return "—";
  return n.toFixed(dp) + "%";
}

// Liquidation prices run to many decimals on low-priced coins, where a fixed
// 4dp would just render "0.0000" — keep significant digits instead.
function fmtPrice(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return "—";
  if (n === 0) return "0";
  return Math.abs(n) < 1
    ? String(parseFloat(n.toPrecision(4)))
    : n.toFixed(4);
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const offsetH = -d.getTimezoneOffset() / 60;
  const utc = `UTC${offsetH >= 0 ? "+" : ""}${offsetH}`;
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())} (${utc})`;
}

function durationSince(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return null;
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  return `${days}d ${hours}h`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Status colours come from the design-system tokens: avocado (success),
// gold (warning) and devil (danger) — see design-system/tokens.css.
function riskColor(label) {
  if (!label) return "#697077";
  if (label === "Low Risk") return "#0F6D32";
  if (label === "Medium Risk" || label === "Moderate") return "#684E00";
  return "#CB1515";
}

function riskMessage(label) {
  if (label === "Low Risk") return "Ok -- high MMR";
  if (label === "Medium Risk" || label === "Moderate") return "Watch out for MMR level.";
  if (label) return "Low Margin, top up your account";
  return "No position data";
}

function riskBg(label) {
  if (label === "Low Risk") return "#EDFDF3";
  if (label === "Medium Risk" || label === "Moderate") return "#FDDC69";
  return "#FCDFDF";
}

// ── Risk Gauge (SVG semicircle) ────────────────────────────────────────────

function arcPath(cx, cy, r, startDeg, endDeg) {
  const toXY = (deg) => ({
    x: cx + r * Math.cos((deg * Math.PI) / 180),
    y: cy - r * Math.sin((deg * Math.PI) / 180),
  });
  const s = toXY(startDeg);
  const e = toXY(endDeg);
  const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${largeArc} 0 ${e.x} ${e.y}`;
}

function RiskGauge({ longMmr, shortMmr, compact = false }) {
  const bothNull = longMmr == null && shortMmr == null;
  const mmr = bothNull
    ? null
    : Math.min(
        longMmr != null ? longMmr : Infinity,
        shortMmr != null ? shortMmr : Infinity
      );

  // Map MMR% to arc angle: 0%→0° (right=danger), 600%+→180° (left=safe)
  const CAP = 600;
  const angleDeg = mmr != null ? Math.min((mmr / CAP) * 180, 180) : 90;

  const cx = 85, cy = 80, r = 62;
  const toXY = (deg) => ({
    x: cx + r * Math.cos((deg * Math.PI) / 180),
    y: cy - r * Math.sin((deg * Math.PI) / 180),
  });
  const needle = toXY(angleDeg);

  // Tick marks at 0°, 60°, 120°, 180°
  const ticks = [0, 60, 120, 180];

  return (
    <svg viewBox="0 0 170 95" className="w-full h-full">
      {/* Zones: right=red(0-60°), middle=yellow(60-120°), left=green(120-180°) */}
      <path d={arcPath(cx, cy, r, 0, 60)}   fill="none" stroke="#FCA5A5" strokeWidth="14" strokeLinecap="round" />
      <path d={arcPath(cx, cy, r, 58, 122)} fill="none" stroke="#FDE68A" strokeWidth="14" strokeLinecap="round" />
      <path d={arcPath(cx, cy, r, 120, 180)} fill="none" stroke="#6EE7B7" strokeWidth="14" strokeLinecap="round" />
      {/* Tick marks */}
      {ticks.map((deg) => (
        <line
          key={deg}
          x1={cx + (r - 10) * Math.cos((deg * Math.PI) / 180)}
          y1={cy - (r - 10) * Math.sin((deg * Math.PI) / 180)}
          x2={cx + (r + 2) * Math.cos((deg * Math.PI) / 180)}
          y2={cy - (r + 2) * Math.sin((deg * Math.PI) / 180)}
          stroke="white" strokeWidth="2"
        />
      ))}
      {/* Needle */}
      <line
        x1={cx} y1={cy}
        x2={needle.x} y2={needle.y}
        stroke="#374151" strokeWidth="2.5" strokeLinecap="round"
      />
      <circle cx={cx} cy={cy} r="5" fill="#374151" />
      {/* Labels — omitted in compact mode, they're illegible at thumbnail size */}
      {!compact && (
        <>
          <text x="10" y="90" fontSize="8" fill="#EF4444" fontWeight="600">HIGH</text>
          <text x={cx - 10} y="16" fontSize="8" fill="#F59E0B" fontWeight="600">MED</text>
          <text x="140" y="90" fontSize="8" fill="#0F6D32" fontWeight="600">LOW</text>
        </>
      )}
    </svg>
  );
}

// ── Cumulative PnL Chart ───────────────────────────────────────────────────

// Round an axis out to human-readable steps (1/2/5 × 10ⁿ) so the gridlines
// land on values like 1000/2000/3000 instead of raw data min/max.
function niceScale(min, max, targetTicks = 5) {
  const span = max - min || Math.abs(max) || 1;
  const rawStep = span / (targetTicks - 1);
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * mag;
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = lo; v <= hi + step * 0.5; v += step) ticks.push(Number(v.toFixed(10)));
  return { ticks, lo, hi: hi === lo ? lo + step : hi };
}

function axisLabel(v) {
  const a = Math.abs(v);
  if (a >= 1000) return `${Number((v / 1000).toFixed(1))}k`;
  if (a >= 1) return String(Number(v.toFixed(0)));
  return String(Number(v.toFixed(2)));
}

function PnlChart({ trades, variant = "trends" }) {
  if (!trades || trades.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-[#697077] text-sm">
        No trade history yet
      </div>
    );
  }

  const sorted = [...trades]
    .filter((t) => t.exit_time && t.profit_loss != null)
    .sort((a, b) => new Date(a.exit_time) - new Date(b.exit_time));

  if (sorted.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-[#697077] text-sm">
        No closed trades yet
      </div>
    );
  }

  const W = 580, H = 180;
  const PL = 38, PR = 12, PT = 12, PB = 26;
  const cW = W - PL - PR;
  const cH = H - PT - PB;

  const spanDays = (new Date(sorted[sorted.length - 1].exit_time) - new Date(sorted[0].exit_time)) / 86400000;
  const fmtX = (t) =>
    new Date(t).toLocaleDateString("en", spanDays > 120 ? { month: "short" } : { month: "short", day: "numeric" });

  // ── Bot Activity: trades per day line chart ──────────────────────────────
  if (variant === "activity") {
    const byDay = {};
    sorted.forEach((t) => {
      const day = t.exit_time.slice(0, 10);
      byDay[day] = (byDay[day] || 0) + 1;
    });
    const days = Object.keys(byDay).sort();
    const dayCounts = days.map((d) => ({ day: d, count: byDay[d] }));

    const { ticks, lo, hi } = niceScale(0, Math.max(...dayCounts.map((d) => d.count)), 5);
    const range = hi - lo || 1;

    const xOf = (i) => PL + (i / Math.max(1, dayCounts.length - 1)) * cW;
    const yOf = (v) => PT + cH - ((v - lo) / range) * cH;
    const baseY = PT + cH;

    const points = dayCounts.map((d, i) => `${xOf(i)},${yOf(d.count)}`).join(" ");
    const area = `M ${xOf(0)} ${baseY} ` +
      dayCounts.map((d, i) => `L ${xOf(i)} ${yOf(d.count)}`).join(" ") +
      ` L ${xOf(dayCounts.length - 1)} ${baseY} Z`;

    const labelCount = Math.min(8, dayCounts.length);
    const xLabels = Array.from({ length: labelCount }, (_, k) => {
      const i = labelCount === 1 ? 0 : Math.round((k / (labelCount - 1)) * (dayCounts.length - 1));
      return { i, text: fmtX(days[i]) };
    });

    return (
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full">
        <defs>
          <linearGradient id="pnl-chart-gradient-activity" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#5B3DF6" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#5B3DF6" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {ticks.map((v) => (
          <line key={v} x1={PL} y1={yOf(v)} x2={W - PR} y2={yOf(v)}
            stroke="#F0F0F3" strokeWidth="1" />
        ))}

        <path d={area} fill="url(#pnl-chart-gradient-activity)" />
        <polyline points={points} fill="none" stroke="#5B3DF6"
          strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />

        {ticks.map((v) => (
          <text key={v} x={PL - 6} y={yOf(v) + 3}
            textAnchor="end" fontSize="9" fill="#9CA3AF">
            {axisLabel(v)}
          </text>
        ))}

        {xLabels.map(({ i, text }, k) => (
          <text key={`${i}-${k}`} x={xOf(i)} y={H - 8}
            textAnchor={k === 0 ? "start" : k === xLabels.length - 1 ? "end" : "middle"}
            fontSize="9" fill="#9CA3AF">
            {text}
          </text>
        ))}
      </svg>
    );
  }

  // ── Profit Trends: daily cumulative PnL line chart ───────────────────────
  const byDay = {};
  let running = 0;
  sorted.forEach((t) => {
    const day = t.exit_time.slice(0, 10);
    running += parseFloat(t.profit_loss || 0);
    byDay[day] = running;
  });
  const trendDays = Object.keys(byDay).sort();
  const cumPnl = trendDays.map((d) => ({ time: d, pnl: byDay[d] })).slice(-30);

  const vals = cumPnl.map((d) => d.pnl);
  const { ticks, lo, hi } = niceScale(Math.min(...vals), Math.max(...vals), 5);
  const range = hi - lo || 1;

  const xOf = (i) => PL + (i / Math.max(1, cumPnl.length - 1)) * cW;
  const yOf = (v) => PT + cH - ((v - lo) / range) * cH;
  const baseY = PT + cH;

  const points = cumPnl.map((d, i) => `${xOf(i)},${yOf(d.pnl)}`).join(" ");
  const area = `M ${xOf(0)} ${baseY} ` +
    cumPnl.map((d, i) => `L ${xOf(i)} ${yOf(d.pnl)}`).join(" ") +
    ` L ${xOf(cumPnl.length - 1)} ${baseY} Z`;

  const lineColor = running >= 0 ? "#0F6D32" : "#EF4444";

  const labelCount = Math.min(8, cumPnl.length);
  const xLabels = Array.from({ length: labelCount }, (_, k) => {
    const i = labelCount === 1 ? 0 : Math.round((k / (labelCount - 1)) * (cumPnl.length - 1));
    return { i, text: fmtX(cumPnl[i].time) };
  });

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full">
      <defs>
        <linearGradient id="pnl-chart-gradient-trends" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lineColor} stopOpacity="0.55" />
          <stop offset="100%" stopColor={lineColor} stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {ticks.map((v) => (
        <line key={v} x1={PL} y1={yOf(v)} x2={W - PR} y2={yOf(v)}
          stroke="#F0F0F3" strokeWidth="1" />
      ))}

      <path d={area} fill="url(#pnl-chart-gradient-trends)" />
      <polyline points={points} fill="none" stroke={lineColor}
        strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />

      {ticks.map((v) => (
        <text key={v} x={PL - 6} y={yOf(v) + 3}
          textAnchor="end" fontSize="9" fill="#9CA3AF">
          {axisLabel(v)}
        </text>
      ))}

      {xLabels.map(({ i, text }, k) => (
        <text key={`${i}-${k}`} x={xOf(i)} y={H - 8}
          textAnchor={k === 0 ? "start" : k === xLabels.length - 1 ? "end" : "middle"}
          fontSize="9" fill="#9CA3AF">
          {text}
        </text>
      ))}
    </svg>
  );
}

// ── Profit Trends Chart (recharts AreaChart, mirrors grid_v2) ──────────────

function ProfitTrendsChart({ trades }) {
  if (!trades || trades.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-[#697077] text-sm">
        No trade history yet
      </div>
    );
  }

  const sorted = [...trades]
    .filter((t) => t.exit_time && t.profit_loss != null)
    .sort((a, b) => new Date(a.exit_time) - new Date(b.exit_time));

  if (sorted.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-[#697077] text-sm">
        No closed trades yet
      </div>
    );
  }

  const byDay = {};
  let running = 0;
  sorted.forEach((t) => {
    const day = t.exit_time.slice(0, 10);
    running += parseFloat(t.profit_loss || 0);
    byDay[day] = running;
  });

  const data = Object.keys(byDay).sort().map((date) => ({
    date: new Date(date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
    profit: parseFloat(byDay[date].toFixed(4)),
  }));

  return (
    <div style={{ width: "100%" }} className="h-[300px] md:h-[400px]">
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 20 }}>
          <defs>
            <linearGradient id="lev-profit-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#34D399" stopOpacity={0.45} />
              <stop offset="95%" stopColor="#34D399" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="date"
            stroke="gray"
            axisLine={false}
            tickLine={false}
            tickMargin={20}
            tick={{ fontSize: 10 }}
          />
          <YAxis
            stroke="gray"
            axisLine={false}
            tickLine={false}
            tickMargin={10}
            tick={{ fontSize: 12 }}
          />
          <CartesianGrid stroke="#f0f0f0" vertical={false} strokeWidth={2} />
          <Tooltip
            formatter={(value) => [`${value} USDT`, "Cumulative Profit"]}
            labelFormatter={(label) => `Date: ${label}`}
            contentStyle={{ fontSize: "12px", borderRadius: "8px", padding: "8px" }}
          />
          <Area
            type="monotone"
            dataKey="profit"
            stroke="#34D399"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#lev-profit-gradient)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Pagination ─────────────────────────────────────────────────────────────

function Pagination({ page, totalPages, onChange, pageSize, onPageSizeChange }) {
  if (totalPages <= 1) return null;
  const pages = [];
  const start = Math.max(1, page - 2);
  const end = Math.min(totalPages, start + 4);
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div className="relative flex items-center justify-center pt-3 border-t border-gray-100">
      <div className="flex items-center gap-1">
        <PageBtn onClick={() => onChange(1)} disabled={page === 1}>«</PageBtn>
        <PageBtn onClick={() => onChange(page - 1)} disabled={page === 1}>‹</PageBtn>
        {start > 1 && <span className="px-1 text-xs text-[#697077]">…</span>}
        {pages.map((p) => (
          <PageBtn key={p} onClick={() => onChange(p)} active={p === page}>
            {p}
          </PageBtn>
        ))}
        {end < totalPages && <span className="px-1 text-xs text-[#697077]">…</span>}
        <PageBtn onClick={() => onChange(page + 1)} disabled={page === totalPages}>›</PageBtn>
        <PageBtn onClick={() => onChange(totalPages)} disabled={page === totalPages}>»</PageBtn>
      </div>
      <select
        value={pageSize}
        onChange={(e) => onPageSizeChange(Number(e.target.value))}
        className="absolute right-0 text-xs text-[#21272A] border border-gray-200 rounded-md px-2 py-1.5 bg-white appearance-none pr-6 focus:outline-none focus:border-[#5B3DF6] cursor-pointer"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23697077' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center' }}
      >
        {[10, 25, 50].map((n) => (
          <option key={n} value={n}>{n}/ page</option>
        ))}
      </select>
    </div>
  );
}

function PageBtn({ children, onClick, disabled, active }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-7 h-7 flex items-center justify-center rounded text-xs font-medium transition-colors ${
        active
          ? "bg-[#5B3DF6] text-white"
          : disabled
          ? "text-gray-300 cursor-default"
          : "text-[#697077] hover:bg-gray-100"
      }`}
    >
      {children}
    </button>
  );
}

// Stats read as a divided vertical list on mobile/tablet and a 3-up grid at desktop.
const STAT_GROUP =
  "flex flex-col divide-y divide-gray-100 [&>*]:py-3 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0 " +
  "desktop:grid desktop:grid-cols-3 desktop:gap-4 desktop:divide-y-0 desktop:[&>*]:py-0";

// ── Stat cell ──────────────────────────────────────────────────────────────

function StatCell({ label, value, sub, tooltip }) {
  return (
    <div>
      <div className="flex items-center gap-1 mb-1">
        <p className="text-[10px] font-semibold text-[#697077] uppercase tracking-wide">{label}</p>
        {tooltip && <CircleInfo className="w-3 h-3 text-[#9CA3AF]" />}
      </div>
      <p className="text-sm font-semibold text-[#21272A]">{value}</p>
      {sub && <p className="text-[10px] text-[#9CA3AF] mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Borrow Details expandable panel ───────────────────────────────────────

function BorrowDetails({ detail }) {
  if (!detail) return null;
  const rows = [
    ["Total Trades", detail.total_trades ?? 0],
    ["Winning Trades", detail.winning_trades ?? 0],
    ["Losing Trades", detail.losing_trades ?? 0],
    ["Stop Loss Hits", detail.stop_loss_count ?? 0],
    ["Total Fees Paid", `${fmtUsdt(detail.total_fees_paid)} USDT`],
    ["Total Interest Paid", `${fmtUsdt(detail.total_interest_paid)} USDT`],
  ];
  return (
    <div className="mt-4 rounded-xl bg-[#F9FAFB] border border-gray-100 divide-y divide-gray-100 text-sm">
      {rows.map(([label, value]) => (
        <div key={label} className="flex justify-between items-center px-4 py-2.5">
          <span className="text-[#697077] text-xs">{label}</span>
          <span className="font-medium text-[#21272A] text-xs">{value}</span>
        </div>
      ))}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

export default function BotDetailPage({ bot, onBack, onStop }) {
  const { authedGet, authedPost } = useAuthedApi();

  const [detail, setDetail] = useState(null);
  const [risk, setRisk] = useState(null);
  const [chartTrades, setChartTrades] = useState([]);
  const [tableData, setTableData] = useState({ results: [], total: 0, total_pages: 1 });
  const [bottomTab, setBottomTab] = useState("history");
  const [chartTab, setChartTab] = useState("trends");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [stopping, setStopping] = useState(false);

  // Load status + position risk + chart trades once
  const isDryRun = bot.config_snapshot?.dry_run === true;

  useEffect(() => {
    authedGet(`/api/borrow/${bot.instance_id}/status/`)
      .then((r) => setDetail(r.data))
      .catch(() => {});
    if (!isDryRun) {
      authedPost("/api/borrow/position-risk/", { symbol: bot.symbol })
        .then((r) => setRisk(r.data))
        .catch(() => {});
    }
    authedGet(`/api/borrow/${bot.instance_id}/trades/?tab=history&page=1&page_size=100`)
      .then((r) => setChartTrades(r.data.results || []))
      .catch(() => {});
  }, [bot.instance_id, bot.symbol]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTable = useCallback(
    (tab, pg, ps) => {
      const url =
        tab === "log"
          ? `/api/borrow/${bot.instance_id}/log/?page=${pg}&page_size=${ps}`
          : `/api/borrow/${bot.instance_id}/trades/?tab=${tab === "open" ? "open" : "history"}&page=${pg}&page_size=${ps}`;
      authedGet(url)
        .then((r) => setTableData(r.data))
        .catch(() => {});
    },
    [bot.instance_id, authedGet]
  );

  // Reload table when tab changes
  useEffect(() => {
    setPage(1);
    fetchTable(bottomTab, 1, pageSize);
  }, [bottomTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (pg) => {
    setPage(pg);
    fetchTable(bottomTab, pg, pageSize);
  };

  const handlePageSizeChange = (ps) => {
    setPageSize(ps);
    setPage(1);
    fetchTable(bottomTab, 1, ps);
  };

  const handleStop = async () => {
    if (!window.confirm(`Stop bot ${bot.symbol}?`)) return;
    setStopping(true);
    try {
      await authedPost("/api/borrow/stop/", { instance_id: bot.instance_id });
      onStop && onStop(bot);
      onBack();
    } catch {
      setStopping(false);
    }
  };

  // Derived display values
  const pair = (bot.symbol || "").replace(/USDT$/, "/USDT");
  const initials = (bot.symbol || "?").replace("USDT", "").slice(0, 3).toUpperCase();
  const isRunning = bot.status === "running";
  const pnl = parseFloat(detail?.realized_pnl || 0);
  const leverageNum = detail?.leverage ?? bot.leverage;
  const dir = (bot.direction || bot.position_side || "").toUpperCase();
  const isDualSide = dir === "LS2" || dir === "LS" || dir === "LS6";
  // initial_capital is stored AFTER leverage for LONG (confirmed: collateral_per_trade
  // x number_of_grids x leverage) but BEFORE leverage for SHORT (confirmed via the
  // creation-form math). Normalize LONG down to before-leverage here so "capital" --
  // and the PNL% derived from it below -- mean the same thing for both directions.
  // LS2/LS6 (dual-side) bots store the SAME post-leverage convention, just doubled across
  // both legs (collateral_per_trade x number_of_grids x leverage x 2) -- confirmed via
  // exact arithmetic match against 3 real LS6 bots -- so dividing by leverage here gives
  // the correct before-leverage total for them too.
  const rawCapital = parseFloat(detail?.initial_capital || bot.initial_capital || 0);
  const capital = ((dir === "LONG" || isDualSide) && leverageNum) ? rawCapital / leverageNum : rawCapital;
  const pnlPct = capital > 0 ? (pnl / capital) * 100 : 0;
  const cfg = detail?.config || {};
  const maxSteps = cfg.max_steps_long ?? cfg.max_steps_short ?? cfg.max_steps ?? null;
  const lastPrice = parseFloat(detail?.last_price || 0);
  const baseCcy = (bot.symbol || "").replace(/USDT$/, "");
  // Capital in base-asset units is fixed at creation (collateral_per_trade x max steps),
  // same as tradeSizeMorpho below -- do not derive it from capital/lastPrice, which drifts
  // with the market even though the MORPHO amount itself never changes.
  const capitalMorpho = dir === "SHORT" && cfg.collateral_per_trade != null && maxSteps != null
    ? cfg.collateral_per_trade * maxSteps
    : (lastPrice > 0 ? capital / lastPrice : null);
  // Trade size after leverage. For SHORT, collateral_per_trade is already base-asset
  // units, so leverage alone gives the base-asset figure; multiply by price for USDT.
  // For LONG, collateral_per_trade is USDT, so leverage alone gives the USDT figure directly.
  const tradeSizeMorpho = dir === "SHORT" && cfg.collateral_per_trade != null && leverageNum != null
    ? cfg.collateral_per_trade * leverageNum
    : null;
  const tradeSizeUsdt = cfg.collateral_per_trade != null && leverageNum != null
    ? (dir === "SHORT" && lastPrice > 0 && tradeSizeMorpho != null
        ? tradeSizeMorpho * lastPrice
        : cfg.collateral_per_trade * leverageNum)
    : null;

  const longRisk = risk?.long;
  const shortRisk = risk?.short;
  const primaryRisk = isDualSide
    ? (longRisk?.mgnPct != null && shortRisk?.mgnPct != null
        ? (longRisk.mgnPct <= shortRisk.mgnPct ? longRisk : shortRisk)
        : longRisk || shortRisk)
    : dir === "SHORT" ? shortRisk : longRisk;

  const floatingPnl = (() => {
    if (isDryRun) return null;
    const currentPrice = parseFloat(detail?.last_price || 0);
    const gridOrders   = detail?.open_orders_detail || [];
    if (currentPrice <= 0 || gridOrders.length === 0) return null;
    const EXIT_FEE_RATE = 0.001;
    return gridOrders.reduce((sum, o) => {
      const ep         = parseFloat(o.entry_price);
      const qty        = parseFloat(o.entry_qty);
      const ef         = parseFloat(o.entry_fee || 0);
      const side       = (o.side || dir).toUpperCase();
      const gross      = side === "SHORT" ? (ep - currentPrice) * qty : (currentPrice - ep) * qty;
      const xfee       = currentPrice * qty * EXIT_FEE_RATE;
      const borrowed   = parseFloat(o.borrowed || 0);
      const hourlyRate = parseFloat(o.hourly_rate || 0);
      const entryMs    = o.entry_timestamp_ms || 0;
      const hoursHeld  = entryMs > 0 ? (Date.now() - entryMs) / 3_600_000 : 0;
      const estInterest = side === "SHORT"
        ? borrowed * hourlyRate * hoursHeld * currentPrice
        : borrowed * hourlyRate * hoursHeld;
      return sum + gross - ef - xfee - estInterest;
    }, 0);
  })();

  // every side holding a position, in LONG→SHORT order
  const riskSides = [
    longRisk?.mgnPct != null && { side: "LONG", r: longRisk },
    shortRisk?.mgnPct != null && { side: "SHORT", r: shortRisk },
  ].filter(Boolean);

  const openOrdersDetail = detail?.open_orders_detail || [];
  const longOpenCount = isDualSide ? openOrdersDetail.filter(o => (o.side || "").toUpperCase() === "LONG").length : null;
  const shortOpenCount = isDualSide ? openOrdersDetail.filter(o => (o.side || "").toUpperCase() === "SHORT").length : null;

  const CHART_PERIODS = ["15Minute", "1Hour", "4Hour", "1Day", "1Week"];
  const BOTTOM_TABS = [
    { key: "history", label: "Trade History" },
    { key: "log", label: "Bot Log" },
    { key: "open", label: "Open Position" },
  ];

  return (
    <div className="flex flex-col gap-4 pb-10">
      {/* ── Back + Header ────────────────────────────────────────── */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm font-medium text-[#697077] hover:text-[#21272A] w-fit"
      >
        <HiArrowLeft className="w-4 h-4" />
        Back
      </button>

      <div className="bg-white rounded-2xl border border-gray-200 p-4 flex items-start gap-3 flex-wrap">
        {/* Avatar */}
        <div className="relative shrink-0">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-sm font-bold text-white">
            {initials}
          </div>
          <img
            src="/icon_okx.svg"
            alt="OKX"
            className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white bg-white"
          />
        </div>
        {/* Name + subtitle */}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#21272A] text-base leading-tight">{pair}</p>
          <p className="text-[11px] text-[#697077] mt-0.5">
            {[detail?.credential_label || bot.credential_label, "Xenoarc", isDualSide ? "Long and Short" : dir || "—"].filter(Boolean).join(" · ")}
            {(detail?.leverage ?? bot.leverage) != null && ` · ${detail?.leverage ?? bot.leverage}x`}
          </p>
        </div>
        {/* Risk readout — one labelled row per open side below desktop, a single
            inline summary at desktop where there's room beside the name */}
        {isRunning && riskSides.length > 0 && !isDualSide && (
          <div className="order-last w-full pt-3 border-t border-gray-100 divide-y divide-gray-100 desktop:hidden">
            {riskSides.map(({ side, r }) => (
              <div key={side} className="flex items-center gap-2 py-2">
                <span className="text-[10px] font-semibold text-[#697077] w-11 shrink-0">{side}</span>
                <div className="w-12 h-7 shrink-0">
                  <RiskGauge longMmr={r.mgnPct} shortMmr={null} compact />
                </div>
                <span
                  className="text-[11px] font-semibold whitespace-nowrap"
                  style={{ color: riskColor(r.riskLabel) }}
                >
                  {fmtPct(r.mgnPct, 1)} {r.riskLabel}
                </span>
                {r.liqPx && (
                  <span className="text-[10px] text-[#9CA3AF] ml-auto text-right">
                    Liq. Price: ${fmtPrice(r.liqPx)}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {primaryRisk && !isDualSide && isRunning && (
          <div className="hidden desktop:flex items-center gap-2 justify-end shrink-0">
            <div className="w-12 h-7 shrink-0">
              <RiskGauge longMmr={primaryRisk.mgnPct} shortMmr={null} compact />
            </div>
            <div className="flex flex-col items-end">
              <span
                className="text-[11px] font-semibold whitespace-nowrap"
                style={{ color: riskColor(primaryRisk.riskLabel) }}
              >
                {fmtPct(primaryRisk.mgnPct, 1)} {primaryRisk.riskLabel}
              </span>
              {primaryRisk.liqPx && (
                <span className="text-[10px] text-[#9CA3AF] whitespace-nowrap">
                  Liq. ${fmtPrice(primaryRisk.liqPx)}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Status + menu */}
        <div className="flex items-center gap-2 shrink-0 justify-end">
          {isRunning
            ? <Badge color="green" type="dot" label="LIVE" size="small" />
            : <Badge color="gray" badgeStyle="lighter" label={bot.status.toUpperCase()} size="small" />
          }
          <div className="relative group">
            <button className="p-1 rounded-lg hover:bg-gray-100 text-[#697077]">
              <HiEllipsisVertical className="w-5 h-5" />
            </button>
            <div className="absolute right-0 top-8 z-10 hidden group-focus-within:block bg-white border border-gray-200 rounded-xl shadow-lg py-1 min-w-[120px]">
              {isRunning && (
                <button
                  onClick={handleStop}
                  disabled={stopping}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  {stopping ? "Stopping…" : "Stop Bot"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Dual-side MMR — hidden for SIM bots and inactive bots */}
      {isDualSide && !isDryRun && isRunning && (
        <div className="bg-white rounded-2xl border border-gray-200 px-4 py-3 flex items-center gap-0">
          {[
            { label: "LONG", r: longRisk },
            { label: "SHORT", r: shortRisk },
          ].map(({ label, r }, i) => (
            <>
              {i === 1 && <div key="divider" className="w-px self-stretch bg-gray-100 mx-4" />}
              <div key={label} className="flex items-center gap-3 flex-1 py-1">
                <span className="text-[11px] font-semibold text-[#697077] w-12 shrink-0">{label}</span>
                <div className="w-14 h-8 shrink-0">
                  <RiskGauge longMmr={r?.mgnPct} shortMmr={null} compact />
                </div>
                {r ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-[#21272A]">{fmtPct(r.mgnPct, 1)}</span>
                    <span className="text-xs font-semibold" style={{ color: riskColor(r.riskLabel) }}>
                      {r.riskLabel}
                    </span>
                  </div>
                ) : (
                  <span className="text-sm text-[#9CA3AF]">No Position</span>
                )}
              </div>
            </>
          ))}
        </div>
      )}

      {/* ── Risk status strip — hidden for SIM bots ── */}
      {!isDryRun && primaryRisk?.riskLabel && (
        <div
          className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-xs font-medium"
          style={{ background: riskBg(primaryRisk.riskLabel), color: riskColor(primaryRisk.riskLabel) }}
        >
          {primaryRisk.riskLabel === "Low Risk"
            ? <CircleCheck className="w-4 h-4 shrink-0" />
            : <HiExclamationTriangle className="w-4 h-4 shrink-0" />}
          {riskMessage(primaryRisk.riskLabel)}
        </div>
      )}

      {/* ── Stats card: Realized Profit / Trading Capital / Floating Profit
           / Winning Rate / Current Value / Position / Buying / Selling /
           Trade Size and Steps ───────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-col gap-4">
        {/* Row 1: Realized Profit / Trading Capital / Floating Profit */}
        <div className={STAT_GROUP}>
          <div>
            <div className="flex items-center gap-1 mb-1">
              <p className="text-[10px] font-semibold text-[#697077] uppercase tracking-wide">Realized Profit</p>
              <CircleInfo className="w-3 h-3 text-[#9CA3AF]" />
            </div>
            <p className={`text-lg font-bold ${pnl >= 0 ? "text-[#302571]" : "text-[#EF4444]"}`}>
              {fmtUsdt(pnl)} USDT
            </p>
            {pnl !== 0 ? (
              <p className={`text-[10px] font-medium ${pnlPct >= 0 ? "text-[#0F6D32]" : "text-[#EF4444]"}`}>
                {pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(2)}%
              </p>
            ) : (
              <p className="text-[10px] text-[#9CA3AF]">No data</p>
            )}
          </div>
          <StatCell
            label="TRADING CAPITAL BEFORE LEVERAGE"
            value={
              dir === "SHORT" && capitalMorpho != null
                ? `${fmtUsdt(capitalMorpho, 2)} ${baseCcy} (= ${fmtUsdt(capital, 2)} USDT)`
                : `${fmtUsdt(capital, 2)} USDT`
            }
            sub={capital === 0 ? "No data" : null}
            tooltip
          />
          <StatCell
            label="Floating Profit"
            value={floatingPnl != null ? `${floatingPnl >= 0 ? "+" : ""}${floatingPnl.toFixed(4)} USDT` : "—"}
            sub={floatingPnl == null ? (isDryRun ? "SIM bot" : "No position") : null}
            tooltip
          />
        </div>

        {/* Row 2: Winning Rate / Current Value / Position */}
        <div className={`${STAT_GROUP} pt-3 border-t border-gray-100`}>
          <StatCell label="Winning Rate" value={`${detail?.win_rate ?? 0}%`} tooltip />
          <StatCell label="Current Value" value={(() => {
            if (detail?.initial_capital == null) return "—";
            const v = parseFloat(detail.initial_capital) + parseFloat(detail?.realized_pnl || 0) + (floatingPnl ?? 0);
            return `${v.toFixed(4)} USDT`;
          })()} sub={detail?.initial_capital == null ? "No data" : null} tooltip />
          <StatCell label="Position" value={detail?.open_orders != null
            ? isDualSide
              ? `${detail.open_orders} open (${longOpenCount}L + ${shortOpenCount}S)`
              : `${detail.open_orders} open`
            : "—"} tooltip />
        </div>

        {/* Row 3: Buying / Selling / Trade Size and Steps */}
        <div className={`${STAT_GROUP} pt-3 border-t border-gray-100`}>
          <div>
            <div className="flex items-center gap-1 mb-1">
              <p className="text-[10px] font-semibold text-[#697077] uppercase tracking-wide">Buying</p>
              <CircleInfo className="w-3 h-3 text-[#9CA3AF]" />
            </div>
            <p className="text-sm font-semibold text-[#21272A]">
              Buy Width {cfg.buy_width != null ? `${cfg.buy_width}%` : "—"}
            </p>
            <p className="text-[10px] text-[#9CA3AF] mt-0.5">
              Buy Confirmation {cfg.buy_confirmation != null ? `${cfg.buy_confirmation}%` : "—"}
            </p>
          </div>
          <div>
            <div className="flex items-center gap-1 mb-1">
              <p className="text-[10px] font-semibold text-[#697077] uppercase tracking-wide">Selling</p>
              <CircleInfo className="w-3 h-3 text-[#9CA3AF]" />
            </div>
            <p className="text-sm font-semibold text-[#21272A]">
              Sell Width {cfg.sell_width != null ? `${cfg.sell_width}%` : "—"}
            </p>
            <p className="text-[10px] text-[#9CA3AF] mt-0.5">
              Sell Confirmation {cfg.sell_confirmation != null ? `${cfg.sell_confirmation}%` : "—"}
            </p>
          </div>
          <div>
            <div className="flex items-center gap-1 mb-1">
              <p className="text-[10px] font-semibold text-[#697077] uppercase tracking-wide">Trade Size and Steps</p>
              <CircleInfo className="w-3 h-3 text-[#9CA3AF]" />
            </div>
            <p className="text-sm font-semibold text-[#21272A]">
              Trade Size {
                dir === "SHORT" && tradeSizeMorpho != null && tradeSizeUsdt != null
                  ? `${fmtUsdt(tradeSizeMorpho, 2)} ${baseCcy} (= ${fmtUsdt(tradeSizeUsdt, 2)} USDT)`
                  : (tradeSizeUsdt != null ? `${fmtUsdt(tradeSizeUsdt, 2)} USDT` : "—")
              }
            </p>
            <p className="text-[10px] text-[#9CA3AF] mt-0.5">
              After leverage · Max. Steps: {detail?.strategy === "borrow_ls6"
                ? `${cfg.max_steps_long ?? "—"} / ${cfg.max_steps_short ?? "—"}`
                : (maxSteps ?? "—")}
            </p>
          </div>
        </div>


        {/* Active since */}
        <p className="text-[10px] text-[#9CA3AF] mt-4 pt-3 border-t border-gray-100">
          Bot active since {fmtDate(detail?.started_at || bot.started_at)}
          {durationSince(detail?.started_at || bot.started_at) && (
            <span> ({durationSince(detail?.started_at || bot.started_at)})</span>
          )}
        </p>
      </div>

      {/* ── Chart ────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4">
        {/* Coin header — ponytail: "More insight" has no destination in this app yet, plain text for now */}
        <div className="flex items-center justify-between pb-4 mb-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-[8px] font-bold text-white shrink-0">
              {initials}
            </div>
            <span className="text-sm font-semibold text-[#21272A]">{pair}</span>
          </div>
          <span className="text-[11px] font-medium text-[#697077] underline">More insight about this coin</span>
        </div>

        {/* Chart type tabs */}
        <div className="flex items-center mb-4">
          {["trends", "activity"].map((key) => (
            <button
              key={key}
              onClick={() => setChartTab(key)}
              className={`mr-5 pb-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                chartTab === key
                  ? "border-[#5B3DF6] text-[#5B3DF6]"
                  : "border-transparent text-[#697077] hover:text-[#21272A]"
              }`}
            >
              {key === "activity" ? "Bot Activity" : "Profit Trends"}
            </button>
          ))}
        </div>

        {/* Chart area */}
        <div className="w-full">
          {chartTab === "trends"
            ? <ProfitTrendsChart trades={chartTrades} />
            : <BotActivityChart bot={bot} trades={chartTrades} detail={detail} defaultPeriod="1D" />
          }
        </div>
      </div>

      {/* ── Orders / History / Log ───────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4">
        {/* Tab bar */}
        <div className="flex items-center border-b border-gray-100 mb-4">
          {BOTTOM_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setBottomTab(t.key)}
              className={`mr-5 pb-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                bottomTab === t.key
                  ? "border-[#5B3DF6] text-[#5B3DF6]"
                  : "border-transparent text-[#697077] hover:text-[#21272A]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Table */}
        {bottomTab === "log" ? (
          <LogTable data={tableData} />
        ) : (
          <TradeTable data={tableData} isOpen={bottomTab === "open"} />
        )}

        <Pagination
          page={page}
          totalPages={tableData.total_pages || 1}
          onChange={handlePageChange}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
    </div>
  );
}

// ── Trade Table ────────────────────────────────────────────────────────────

function DirectionPill({ dir }) {
  const d = (dir || "").toUpperCase();
  if (!d) {
    return (
      <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full border border-transparent whitespace-nowrap text-[#9CA3AF]">
        —
      </span>
    );
  }
  const color = d === "SHORT" ? "#9E1F63" : d === "LONG" ? "#007A78" : "#5C5C5C";
  return (
    <span
      className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap"
      style={{ color, borderColor: color }}
    >
      {d}
    </span>
  );
}

// Mobile: the wide table can't be read at 412px, so collapse each trade to
// ID / direction / quantity / PNL and tuck the rest behind a disclosure row.
function MobileTradeList({ rows, isOpen }) {
  const [expanded, setExpanded] = useState(null);

  if (rows.length === 0) {
    return (
      <p className="py-8 text-center text-[#697077] text-sm">
        {isOpen ? "No open positions" : "No closed trades yet"}
      </p>
    );
  }

  // quote qty isn't served separately — it's the notional, base qty × entry price
  const quoteQty = (row) => {
    if (row.quantity_quote != null) return row.quantity_quote;
    const q = parseFloat(row.quantity ?? row.amount);
    const p = parseFloat(row.entry_price);
    return isNaN(q) || isNaN(p) ? null : q * p;
  };

  const detailRows = (row) =>
    isOpen
      ? [
          ["QUANTITY (BASE)", row.quantity ?? row.amount ?? "—"],
          ["QUANTITY (QUOTE)", quoteQty(row) != null ? `${fmtUsdt(quoteQty(row), 5)} USDT` : "—"],
          ["ENTRY PRICE", row.entry_price ?? "—"],
          ["FEE", row.entry_fee != null ? `${row.entry_fee} USDT` : "—"],
          ["ENTRY TIME", fmtDateTime(row.entry_time)],
        ]
      : [
          ["ENTRY PRICE", row.entry_price ?? "—"],
          ["EXIT PRICE", row.exit_price ?? "—"],
          ["INTEREST", row.interest != null ? `${row.interest} USDT` : "—"],
          ["TRADING FEE", row.trading_fee != null ? `${row.trading_fee} USDT` : "—"],
          ["ENTRY TIME", fmtDateTime(row.entry_time)],
          ["EXIT TIME", row.exit_time ? fmtDateTime(row.exit_time) : "—"],
        ];

  const COLS = "grid grid-cols-[2.5rem_4.5rem_1fr_1.25rem] gap-2 items-center";

  return (
    <div className="desktop:hidden">
      <div className={`${COLS} pb-2 border-b border-gray-100 text-[10px] font-semibold text-[#697077] uppercase tracking-wide`}>
        <span>ID</span>
        <span>Direction</span>
        <span>{isOpen ? "Quantity Base" : "Quantity"}</span>
        <span />
      </div>
      {rows.map((row, idx) => {
        const key = row.id ?? row.no ?? idx;
        const pnl = row.profit_loss != null ? parseFloat(row.profit_loss) : null;
        const pnlPct = row.pnl_pct != null ? parseFloat(row.pnl_pct) : null;
        const isExp = expanded === key;
        return (
          <div key={key} className="border-b border-gray-50">
            <button
              type="button"
              onClick={() => setExpanded(isExp ? null : key)}
              aria-expanded={isExp}
              className={`${COLS} w-full py-2.5 text-left text-xs`}
            >
              <span className="font-medium text-[#21272A]">#{row.id ?? row.no}</span>
              <span><DirectionPill dir={row.direction} /></span>
              <span className="text-[#21272A] truncate">{row.quantity ?? row.amount ?? "—"}</span>
              <HiChevronDown
                className={`w-4 h-4 text-[#697077] transition-transform ${isExp ? "rotate-180" : ""}`}
              />
            </button>
            {isExp && (
              <div className="bg-[#F9FAFB] rounded-lg px-3 py-1 mb-2.5 divide-y divide-gray-100">
                {!isOpen && (
                  <div className="flex items-center justify-between py-2 text-[11px]">
                    <span className="text-[#697077] uppercase tracking-wide">PNL</span>
                    {pnl != null ? (
                      <span className={`font-medium ${pnl >= 0 ? "text-[#0F6D32]" : "text-[#EF4444]"}`}>
                        {fmtUsdt(pnl)} USDT
                        {pnlPct != null && ` (${pnlPct >= 0 ? "+" : ""}${pnlPct.toFixed(2)}%)`}
                      </span>
                    ) : (
                      <span className="text-[#697077]">—</span>
                    )}
                  </div>
                )}
                {detailRows(row).map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between py-2 text-[11px]">
                    <span className="text-[#697077] uppercase tracking-wide">{label}</span>
                    <span className="text-[#21272A] text-right">{value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TradeTable({ data, isOpen }) {
  const rows = data?.results || [];

  const openHeaders = ["ID", "DIRECTION", "QUANTITY (BASE)", "QUANTITY (QUOTE)", "ENTRY PRICE", "FEE", "ENTRY TIME"];
  const histHeaders = ["ID", "DIRECTION", "QUANTITY", "PNL", "ENTRY PRICE", "EXIT PRICE", "INTEREST", "TRADING FEE", "ENTRY TIME", "EXIT TIME"];
  const headers = isOpen ? openHeaders : histHeaders;

  return (
    <>
    <MobileTradeList rows={rows} isOpen={isOpen} />
    <div className="hidden desktop:block overflow-x-auto">
      <table className="w-full text-xs min-w-[860px]">
        <thead>
          <tr className="border-b border-gray-100">
            {headers.map((h) => (
              <th key={h} className="pb-2.5 text-left font-semibold text-[10px] text-[#697077] uppercase tracking-wide pr-3 whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={headers.length} className="py-8 text-center text-[#697077] text-sm">
                {isOpen ? "No open positions" : "No closed trades yet"}
              </td>
            </tr>
          ) : isOpen ? (
            rows.map((row, idx) => {
              const quoteQty = row.quantity_quote != null
                ? row.quantity_quote
                : (() => { const q = parseFloat(row.quantity ?? row.amount); const p = parseFloat(row.entry_price); return isNaN(q) || isNaN(p) ? null : q * p; })();
              return (
                <tr key={row.id ?? row.no ?? idx} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="py-2.5 pr-3 font-medium text-[#21272A]">#{row.id ?? row.no}</td>
                  <td className="py-2.5 pr-3"><DirectionPill dir={row.direction} /></td>
                  <td className="py-2.5 pr-3 text-[#21272A]">{row.quantity ?? row.amount ?? "—"}</td>
                  <td className="py-2.5 pr-3 text-[#21272A]">{quoteQty != null ? `${fmtUsdt(quoteQty, 5)} USDT` : "—"}</td>
                  <td className="py-2.5 pr-3 text-[#21272A]">{row.entry_price ?? "—"}</td>
                  <td className="py-2.5 pr-3 text-[#697077]">{row.entry_fee != null ? `${row.entry_fee} USDT` : "—"}</td>
                  <td className="py-2.5 pr-3 text-[#697077] whitespace-nowrap">{fmtDateTime(row.entry_time)}</td>
                </tr>
              );
            })
          ) : (
            rows.map((row, idx) => {
              const pnl = row.profit_loss != null ? parseFloat(row.profit_loss) : null;
              const pnlPct = row.pnl_pct != null ? parseFloat(row.pnl_pct) : null;
              return (
                <tr key={row.id ?? row.no ?? idx} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="py-2.5 pr-3 font-medium text-[#21272A]">#{row.id ?? row.no}</td>
                  <td className="py-2.5 pr-3"><DirectionPill dir={row.direction} /></td>
                  <td className="py-2.5 pr-3 text-[#21272A]">{row.quantity ?? row.amount ?? "—"}</td>
                  <td className="py-2.5 pr-3">
                    {pnl != null ? (
                      <span className={`font-medium ${pnl >= 0 ? "text-[#0F6D32]" : "text-[#EF4444]"}`}>
                        {fmtUsdt(pnl)} USDT{pnlPct != null && ` (${pnlPct >= 0 ? "+" : ""}${pnlPct.toFixed(2)}%)`}
                      </span>
                    ) : <span className="text-[#697077]">—</span>}
                  </td>
                  <td className="py-2.5 pr-3 text-[#21272A]">{row.entry_price ?? "—"}</td>
                  <td className="py-2.5 pr-3 text-[#21272A]">{row.exit_price ?? "—"}</td>
                  <td className="py-2.5 pr-3 text-[#697077]">{row.interest != null ? `${row.interest} USDT` : "—"}</td>
                  <td className="py-2.5 pr-3 text-[#697077]">{row.trading_fee != null ? `${row.trading_fee} USDT` : "—"}</td>
                  <td className="py-2.5 pr-3 text-[#697077] whitespace-nowrap">{fmtDateTime(row.entry_time)}</td>
                  <td className="py-2.5 pr-3 text-[#697077] whitespace-nowrap">{row.exit_time ? fmtDateTime(row.exit_time) : "—"}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
    </>
  );
}

// ── Bot Log Table ──────────────────────────────────────────────────────────

function extractDirection(message) {
  if (!message) return null;
  const m = message.toUpperCase();
  if (m.includes("SHORT")) return "SHORT";
  if (m.includes("LONG")) return "LONG";
  return null;
}

function LogTable({ data }) {
  const rows = data?.results || [];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs min-w-[560px]">
        <thead>
          <tr className="border-b border-gray-100">
            {["ID", "DIRECTION", "TYPE", "LOG DETAIL", "UPDATED AT"].map((h) => (
              <th key={h} className="pb-2.5 text-left font-semibold text-[10px] text-[#697077] uppercase tracking-wide pr-3 whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={5} className="py-8 text-center text-[#697077] text-sm">
                No activity logged yet
              </td>
            </tr>
          ) : (
            rows.map((row, idx) => {
              const dir = extractDirection(row.message);
              return (
                <tr key={row.id ?? row.no ?? idx} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-2.5 pr-3 font-medium text-[#21272A]">#{row.id ?? row.no}</td>
                  <td className="py-2.5 pr-3"><DirectionPill dir={dir} /></td>
                  <td className="py-2.5 pr-3 text-[#697077] font-mono text-[9px] whitespace-nowrap">
                    {row.event_type ?? "—"}
                  </td>
                  <td className="py-2.5 pr-3 max-w-[280px]">
                    <p className="text-[#21272A] truncate">{row.message ?? "—"}</p>
                  </td>
                  <td className="py-2.5 text-[#697077] whitespace-nowrap">{fmtDateTime(row.timestamp ?? row.created_at)}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
